<template>
  <div
    class="dropzone"
    :class="`dropzone--${currentState}`"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragEnter"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <input
      ref="input"
      type="file"
      class="dropzone__input"
      :accept="accept"
      @change="onFileInputChange"
    >
    <div class="dropzone__icon">
      <template v-if="currentState === 'loaded'">
        ✓
      </template>
      <template v-else-if="currentState === 'error'">
        ✕
      </template>
      <template v-else>
        ⇧
      </template>
    </div>
    <div class="dropzone__label">
      <template v-if="currentState === 'loaded'">
        {{ displayName }}
      </template>
      <template v-else-if="currentState === 'error'">
        Arquivo não permitido
      </template>
      <template v-else-if="currentState === 'hover'">
        Solte para enviar
      </template>
      <template v-else>
        {{ instructionText }}
      </template>
    </div>
    <div class="dropzone__sub">
      <template v-if="currentState === 'loaded'">
        {{ displaySize }} · pronto
      </template>
      <template v-else-if="currentState === 'error'">
        {{ errorMessage }}
      </template>
      <template v-else>
        {{ idleSubText }}
      </template>
    </div>
    <button
      v-if="currentState === 'loaded' || currentState === 'error'"
      type="button"
      class="dropzone__remove"
      aria-label="Remover arquivo"
      title="Remover arquivo"
      @click.stop="clear"
    >
      ✕
    </button>
  </div>
</template>

<script>
export default {
  name: 'BaseDropzone',
  props: {
    accept: {
      type: String,
      required: false,
      default: ''
    },
    instructionText: {
      type: String,
      required: false,
      default: 'Arraste o arquivo aqui'
    },
    subText: {
      type: String,
      required: false,
      default: ''
    },
    fileName: {
      type: String,
      required: false,
      default: ''
    },
    fileSize: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      isDragging: false,
      selectedFile: null,
      errorMessage: ''
    }
  },
  computed: {
    displayName() {
      return this.selectedFile ? this.selectedFile.name : this.fileName
    },
    displaySize() {
      return this.selectedFile ? this.formatFileSize(this.selectedFile.size) : this.fileSize
    },
    idleSubText() {
      return [this.subText, this.accept].filter(Boolean).join(' · ')
    },
    acceptedRules() {
      return this.accept
        .split(',')
        .map((rule) => rule.trim().toLowerCase())
        .filter(Boolean)
    },
    currentState() {
      if (this.errorMessage) {
        return 'error'
      }
      if (this.displayName) {
        return 'loaded'
      }
      return this.isDragging ? 'hover' : 'idle'
    }
  },
  methods: {
    onDragEnter() {
      this.isDragging = true
      this.errorMessage = ''
    },
    onDragLeave(event) {
      if (event.relatedTarget && this.$el.contains(event.relatedTarget)) {
        return
      }
      this.isDragging = false
    },
    onDrop(event) {
      this.isDragging = false
      this.handleFile(event.dataTransfer.files[0])
    },
    onFileInputChange(event) {
      this.handleFile(event.target.files[0])
    },
    handleFile(file) {
      if (!file) {
        return
      }
      if (!this.isFileAccepted(file)) {
        this.selectedFile = null
        this.errorMessage = `Formato não permitido. Aceitos: ${this.accept}`
        return
      }
      this.errorMessage = ''
      this.selectedFile = file
      this.$emit('file-selected', file)
    },
    clear() {
      this.selectedFile = null
      this.errorMessage = ''
      if (this.$refs.input) {
        this.$refs.input.value = ''
      }
      this.$emit('cleared')
    },
    isFileAccepted(file) {
      if (!this.acceptedRules.length) {
        return true
      }
      const fileName = file.name.toLowerCase()
      const fileType = (file.type || '').toLowerCase()
      return this.acceptedRules.some((rule) => {
        if (rule.startsWith('.')) {
          return fileName.endsWith(rule)
        }
        if (rule.endsWith('/*')) {
          return fileType.startsWith(rule.replace('/*', '/'))
        }
        return fileType === rule
      })
    },
    formatFileSize(bytes) {
      if (bytes < 1024) {
        return `${bytes} B`
      }
      const units = ['KB', 'MB', 'GB']
      let value = bytes / 1024
      let unitIndex = 0
      while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024
        unitIndex += 1
      }
      return `${value.toFixed(1).replace('.', ',')} ${units[unitIndex]}`
    }
  }
}
</script>

<style lang="scss">
.dropzone {
  position: relative;
  border: 2px dashed var(--ds-color-black-010);
  border-radius: 8px;
  padding: 26px 16px;
  text-align: center;
  background: var(--ds-color-black-005);

  &__input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }

  &__icon {
    font-size: 22px;
    margin-bottom: 8px;
    color: var(--ds-color-black-040);
  }

  &__label {
    font-size: 0.875rem;
    font-weight: 600;
    margin-bottom: 2px;
    color: var(--v-textPrimary-base);
  }

  &__sub {
    font-size: 0.8rem;
    color: var(--ds-color-black-040);
  }

  &__remove {
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 2;
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
    color: var(--v-error-base);
    background: var(--ds-color-white);
    border: 1px solid var(--ds-color-black-010);
    border-radius: 50%;
    cursor: pointer;
    padding: 0;

    &:hover {
      background: rgba(235, 104, 104, 0.12);
      border-color: var(--v-error-base);
    }
  }

  &--hover {
    border-color: var(--v-bluefa-base);
    background: rgba(53, 168, 250, 0.08);

    .dropzone__icon {
      color: var(--v-bluefa-base);
    }
  }

  &--loaded {
    border-color: var(--v-success-base);
    background: rgba(76, 175, 80, 0.08);

    .dropzone__icon {
      color: var(--v-success-base);
    }
  }

  &--error {
    border-color: var(--v-error-base);
    background: rgba(235, 104, 104, 0.08);

    .dropzone__icon {
      color: var(--v-error-base);
    }

    .dropzone__label {
      color: var(--v-error-base);
    }
  }
}
</style>
